/*
 * <soft-skin-quiz> — shows one quiz step at a time over the markup rendered
 * by sections/soft-skin-quiz.liquid. Path and recommendation rules live in
 * soft-skin-quiz-engine.js (window.CheekySoftSkinQuiz).
 *
 * Privacy: answers stay in this tab's sessionStorage. They are never put in
 * the offer link and never sent with analytics events.
 */
(function () {
  'use strict';

  if (customElements.get('soft-skin-quiz')) return;

  var STORAGE_KEY = 'cheeky:soft-skin-quiz:v1';
  var STORAGE_VERSION = 1;
  var STOCK_CHECK_TIMEOUT_MS = 2500;
  var AUTO_ADVANCE_DELAY_MS = 250;
  /* Long enough to read the loading line; the stock check usually finishes sooner. */
  var BUILDING_MIN_MS = 1200;

  var DRY_GOALS = ['tight', 'rough', 'later'];

  var PROFILE_LABELS = {
    skin_goal: {
      tight: 'Lose the tight, dry feeling',
      rough: 'Smoother, less flaky-feeling skin',
      later: 'Skin that still feels good hours later',
      softer: 'Skin that feels moisturized'
    },
    current_cleanser: {
      body_wash: 'Body wash',
      regular_bar: 'A regular bar',
      combo: 'A 2-in-1 or 3-in-1',
      whatever: 'Whatever’s in the shower'
    }
  };

  var STAGE_LABELS = { routine: 'Your skin', preferences: 'Your preferences', bars: 'Your routine' };

  var GOAL_COPY = {
    tight: 'To stop that tight, dry feeling after a shower, every bar is made with moisturizing ingredients like shea butter and jojoba oil.',
    rough: 'To stop rough or flaky-feeling skin, every bar is made with moisturizing ingredients like shea butter and jojoba oil.',
    later: 'To keep your skin feeling moisturized all day, every bar is made with long-lasting moisturizers like shea butter and jojoba oil.',
    softer: 'For skin that feels soft and moisturized, every bar is made with hydrating ingredients like shea butter and jojoba oil.'
  };

  class SoftSkinQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.engine = window.CheekySoftSkinQuiz;
      if (!this.engine) {
        console.error('soft-skin-quiz: engine script did not load');
        return;
      }

      this.config = this.readConfig();
      if (!this.config) return;

      this.answers = {};
      this.firstStep = this.engine.getPath({})[0];
      this.step = this.firstStep;
      this.started = false;
      this.advanceTimer = null;
      this.autoAdvance = this.dataset.autoAdvance === 'true';
      this.snapshot = null;
      this.stepStartedAt = Date.now();
      this.live = this.querySelector('[data-ssq-live]');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.onPopState = this.onPopState.bind(this);
      window.addEventListener('popstate', this.onPopState);

      this.restore();
      this.track('quiz_view');

      /* A refresh keeps this tab's history entry, so go straight back to that step. */
      var historyState = window.history.state && window.history.state.softSkinQuiz;
      if (historyState && Object.keys(this.answers).length) this.resume({ push: false });
      else this.goTo(this.firstStep, { push: false, initial: true });
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    readConfig() {
      var node = this.querySelector('[data-ssq-config]');
      try {
        var parsed = JSON.parse(node.textContent);
        parsed.ruleVersion = this.dataset.ruleVersion || '1';
        return parsed;
      } catch (error) {
        console.error('soft-skin-quiz: could not read the quiz configuration', error);
        return null;
      }
    }

    /* ---------- storage ---------- */

    restore() {
      var saved = null;
      try {
        saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
      } catch (error) {
        console.warn('soft-skin-quiz: saved progress could not be read, starting fresh', error);
      }
      if (!saved || saved.v !== STORAGE_VERSION || saved.ruleVersion !== this.config.ruleVersion) return;
      var savedAnswers = this.engine.normalizeAnswers(saved.answers);
      if (!Object.keys(savedAnswers).length) return;
      this.answers = savedAnswers;
      this.savedStep = typeof saved.step === 'string' ? saved.step : null;
      this.savedSnapshot = saved.snapshot || null;
      var resume = this.querySelector('[data-ssq-resume]');
      if (resume) resume.hidden = false;
    }

    persist() {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          v: STORAGE_VERSION,
          ruleVersion: this.config.ruleVersion,
          answers: this.answers,
          step: this.step,
          snapshot: this.snapshot
        }));
      } catch (error) {
        console.warn('soft-skin-quiz: progress could not be saved in this tab', error);
      }
    }

    /* ---------- events ---------- */

    onClick(event) {
      /*
       * A tap or mouse click on an answer moves on by itself. Keyboard
       * selection (event.detail is 0) never does; it shows Continue instead.
       */
      var option = event.target.closest('.ssq-option');
      if (option && this.contains(option)) {
        if (this.autoAdvance && event.detail > 0 && !this.engine.isMultiSelect(this.step)) this.scheduleAdvance(option);
        return;
      }

      var target = event.target.closest('button, a');
      if (!target || !this.contains(target)) return;

      if (target.hasAttribute('data-ssq-resume-button')) {
        this.resume();
      } else if (target.hasAttribute('data-ssq-back')) {
        var previous = this.engine.previousStep(this.answers, this.step);
        if (previous !== 'landing') this.goTo(previous, { push: true });
      } else if (target.hasAttribute('data-ssq-continue')) {
        this.advance();
      } else if (target.hasAttribute('data-ssq-edit')) {
        this.snapshot = null;
          this.goTo(this.firstStep, { push: true });
      } else if (target.hasAttribute('data-ssq-edit-scents')) {
        this.snapshot = null;
        this.goTo('scent_notes', { push: true });
      } else if (target.hasAttribute('data-ssq-offer-link')) {
        if (!this.snapshot || !target.getAttribute('href')) {
          event.preventDefault();
          return;
        }
        this.track('quiz_offer_click', { signature: this.engine.signature(this.snapshot.slots) });
      }
    }

    onSubmit(event) {
      var form = event.target.closest('[data-ssq-step]');
      if (!form) return;
      event.preventDefault();
      this.advance();
    }

    onChange(event) {
      var input = event.target;
      if (!input.matches || !input.matches('.ssq-option__input')) return;
      var form = input.closest('[data-ssq-step]');
      if (!form) return;
      this.setError(form, false);
      /* A pending tap-to-advance moves on by itself, so Continue stays hidden for it. */
      if (!this.engine.isMultiSelect(form.getAttribute('data-ssq-step'))) this.setDock(form, !this.advanceTimer);
    }

    scheduleAdvance(option) {
      var self = this;
      var step = this.step;
      if (this.advanceTimer) window.clearTimeout(this.advanceTimer);
      this.advanceTimer = window.setTimeout(function () {
        self.advanceTimer = null;
        var input = option.querySelector('.ssq-option__input');
        if (self.step === step && input && input.checked) self.advance();
      }, AUTO_ADVANCE_DELAY_MS);
    }

    setDock(node, visible) {
      var dock = node.querySelector('[data-ssq-dock]');
      if (dock) dock.hidden = !visible;
    }

    onPopState(event) {
      var state = event.state && event.state.softSkinQuiz;
      if (!state) {
        this.goTo(this.firstStep, { push: false });
        return;
      }
      if (state === 'results' || state === 'unfulfillable' || state === 'checking') {
        if (this.engine.isComplete(this.answers)) this.showResults({ push: false });
        else this.goTo(this.firstStep, { push: false });
        return;
      }
      /* An entry for a question the current answers no longer ask falls back to the open question. */
      var path = this.engine.getPath(this.answers);
      if (path.indexOf(state) === -1) state = this.firstUnanswered(path);
      this.goTo(state, { push: false });
    }

    /* ---------- navigation ---------- */

    resume(options) {
      var push = !options || options.push !== false;
      var step = this.savedStep;
      if (step === 'results' || step === 'unfulfillable' || step === 'checking') {
        if (this.engine.isComplete(this.answers)) {
          this.snapshot = this.savedSnapshot || null;
          this.showResults({ push: push });
          return;
        }
        step = null;
      }
      var path = this.engine.getPath(this.answers);
      if (!step || path.indexOf(step) === -1) step = this.firstUnanswered(path);
      this.goTo(step, { push: push });
    }

    firstUnanswered(path) {
      for (var i = 0; i < path.length; i += 1) {
        if (this.engine.isQuestion(path[i]) && !this.answers[path[i]]) return path[i];
      }
      return path[path.length - 1];
    }

    advance() {
      var current = this.step;
      if (this.advanceTimer) {
        window.clearTimeout(this.advanceTimer);
        this.advanceTimer = null;
      }
      if (this.engine.isQuestion(current)) {
        var form = this.stepNode(current);
        var checked = Array.prototype.slice.call(form.querySelectorAll('.ssq-option__input:checked'));
        if (!checked.length) {
          this.setError(form, true);
          return;
        }
        var updated = Object.assign({}, this.answers);
        updated[current] = this.engine.isMultiSelect(current)
          ? checked.map(function (input) { return input.value; })
          : checked[0].value;
        this.answers = this.engine.normalizeAnswers(updated);
        this.snapshot = null;
          if (!this.started) {
          this.started = true;
          this.track('quiz_start');
        }
      }
      this.track('quiz_step_complete', { step: current, elapsedMs: Date.now() - this.stepStartedAt });
      var next = this.engine.nextStep(this.answers, current);
      if (next === 'results') this.showResults({ push: true });
      else this.goTo(next, { push: true });
    }

    setError(form, visible) {
      var error = form.querySelector('[data-ssq-error]');
      if (!error) return;
      error.hidden = !visible;
      if (visible) {
        var first = form.querySelector('.ssq-option__input');
        if (first) first.focus();
      }
    }

    stepNode(step) {
      return this.querySelector('[data-ssq-step="' + step + '"]');
    }

    goTo(step, options) {
      var node = this.stepNode(step);
      if (!node) return;
      this.step = step;
      this.stepStartedAt = Date.now();

      this.querySelectorAll('[data-ssq-step]').forEach(function (other) {
        other.hidden = other !== node;
      });

      if (this.engine.isQuestion(step)) {
        var answer = this.answers[step];
        node.querySelectorAll('.ssq-option__input').forEach(function (input) {
          input.checked = Array.isArray(answer) ? answer.indexOf(input.value) !== -1 : input.value === answer;
        });
        this.setError(node, false);
        /* An answered question needs a way forward. An open one waits for a pick. */
        this.setDock(node, Boolean(answer) || !this.autoAdvance);
      }

      var resumeLink = this.querySelector('[data-ssq-resume]');
      if (resumeLink && !(options && options.initial)) resumeLink.hidden = true;

      var back = this.querySelector('.ssq-topbar [data-ssq-back]');
      if (back) back.hidden = step === this.firstStep || step === 'checking';

      /* The proof screen speaks to both answers so far: copy follows what they
         wash with now (question 2), the review follows their skin goal (question 1). */
      var dryGoal = DRY_GOALS.indexOf(this.answers.skin_goal) !== -1;
      node.querySelectorAll('[data-ssq-goal-group]').forEach(function (group) {
        group.hidden = (group.getAttribute('data-ssq-goal-group') === 'dry') !== dryGoal;
      });
      var cleanser = this.answers.current_cleanser;
      node.querySelectorAll('[data-ssq-cleanser-group]').forEach(function (group) {
        group.hidden = group.getAttribute('data-ssq-cleanser-group') !== cleanser;
      });

      this.renderProgress(step);

      if (options && options.push) window.history.pushState({ softSkinQuiz: step }, '');

      if (step !== 'checking' && !(options && options.initial)) this.persist();
      if (step !== 'checking') this.track('quiz_step_view', { step: step });

      var focusTarget = Array.prototype.filter.call(node.querySelectorAll('[data-ssq-focus]'), function (candidate) {
        return !candidate.closest('[hidden]');
      })[0];
      if (focusTarget && !(options && options.initial)) {
        focusTarget.focus({ preventScroll: true });
        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }

    renderProgress(step) {
      var progress = this.querySelector('[data-ssq-progress]');
      var label = this.querySelector('[data-ssq-stage-label]');
      if (step === 'checking') return;
      var onFirst = step === this.firstStep;
      var path = this.engine.getPath(this.answers);
      var index = path.indexOf(step);
      /* Results and the out-of-stock screen sit at the end of the path. */
      var percent = index === -1 ? 100 : Math.round((index / path.length) * 100);
      if (progress) {
        progress.hidden = onFirst;
        progress.setAttribute('aria-valuenow', String(percent));
        var fill = progress.querySelector('[data-ssq-progress-fill]');
        if (fill) fill.style.width = percent + '%';
      }
      if (label) label.textContent = onFirst ? '' : (STAGE_LABELS[this.engine.stageFor(step)] || '');
    }

    /* ---------- results ---------- */

    showResults(options) {
      var self = this;
      var push = !options || options.push !== false;
      /* A snapshot frozen earlier in this tab stays as shown while it can still be supplied. */
      if (this.snapshot && this.snapshotStillValid(this.snapshot)) {
        this.renderResult(this.snapshot, push);
        return;
      }
      this.snapshot = null;

      var first = this.engine.recommend(this.answers, this.config.catalog, this.config);
      if (first.status !== 'ok') {
        this.renderUnfulfillable(first, push);
        return;
      }

      this.goTo('checking', { push: false });
      var minimumWait = new Promise(function (resolve) { window.setTimeout(resolve, BUILDING_MIN_MS); });
      Promise.all([this.confirmStock(first.items), minimumWait]).then(function () {
        var confirmed = self.engine.recommend(self.answers, self.config.catalog, self.config);
        if (confirmed.status === 'ok') self.renderResult(confirmed, push);
        else self.renderUnfulfillable(confirmed, push);
      });
    }

    snapshotStillValid(snapshot) {
      var self = this;
      if (!snapshot || snapshot.status !== 'ok' || !Array.isArray(snapshot.items)) return false;
      if (snapshot.ruleVersion !== this.config.ruleVersion) return false;
      return snapshot.items.every(function (item) {
        var product = self.config.catalog.filter(function (entry) {
          return entry.handle === item.handle && String(entry.variantId) === item.variantId;
        })[0];
        return self.engine.canSupply(product, item.quantity, self.config);
      });
    }

    /*
     * Ask the storefront whether the picked bars are still in stock. The page
     * data can be a few minutes old. A failed or slow check keeps the page
     * data; the offer page checks stock again before anything is added.
     */
    confirmStock(items) {
      var self = this;
      var rootUrl = this.dataset.rootUrl || '/';
      if (rootUrl.charAt(rootUrl.length - 1) !== '/') rootUrl += '/';

      var checks = items.map(function (item) {
        var controller = typeof AbortController === 'function' ? new AbortController() : null;
        var timer = controller ? window.setTimeout(function () { controller.abort(); }, STOCK_CHECK_TIMEOUT_MS) : null;
        return fetch(rootUrl + 'products/' + encodeURIComponent(item.handle) + '.js', {
          credentials: 'same-origin',
          signal: controller ? controller.signal : undefined
        })
          .then(function (response) {
            if (!response.ok) throw new Error('stock check returned ' + response.status);
            return response.json();
          })
          .then(function (product) {
            var variant = (product.variants || []).filter(function (entry) {
              return String(entry.id) === item.variantId;
            })[0];
            var inStock = Boolean(variant && variant.available);
            self.config.catalog.forEach(function (entry) {
              if (entry.handle === item.handle && !inStock) entry.available = false;
            });
          })
          .catch(function (error) {
            console.warn('soft-skin-quiz: stock check failed for ' + item.handle + ', using page data', error);
          })
          .then(function () {
            if (timer) window.clearTimeout(timer);
          });
      });
      return Promise.all(checks);
    }

    titleFor(handle) {
      var match = this.config.catalog.filter(function (entry) { return entry.handle === handle; })[0];
      return match ? match.title : handle;
    }

    /* How a scent reads inside the "You picked …" sentence. */
    noteInSentence(id) {
      return id === 'unscented' ? 'unscented' : this.noteLabel(id).toLowerCase();
    }

    noteLabel(id) {
      var match = (this.config.notes || []).filter(function (note) { return note.id === id; })[0];
      return match ? match.label : id;
    }

    joinList(parts) {
      if (parts.length < 2) return parts.join('');
      if (parts.length === 2) return parts.join(' and ');
      return parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1];
    }

    resultCopy(result) {
      var self = this;
      var missed = result.missedNotes.length
        ? 'Our ' + this.joinList(result.missedNotes.map(function (id) { return self.noteInSentence(id); }))
          + (result.missedNotes.length === 1 ? ' bar is' : ' bars are') + ' out of stock right now, so we left '
          + (result.missedNotes.length === 1 ? 'it' : 'them') + ' out.'
        : '';
      return {
        title: 'Your dry-skin routine is ready',
        missed: missed,
        scent: this.joinList(result.pickedNotes.map(function (id) { return self.noteLabel(id); })),
        goal: GOAL_COPY[this.answers.skin_goal] || ''
      };
    }

    renderResult(result, push) {
      var self = this;
      /* Freeze first. The list and the offer link are both written from this one object. */
      this.snapshot = result;
      var copy = this.resultCopy(result);
      var node = this.stepNode('results');

      node.querySelector('[data-ssq-result-title]').textContent = copy.title;
      var goal = node.querySelector('[data-ssq-result-goal]');
      goal.textContent = copy.goal;
      goal.hidden = !copy.goal;
      var missed = node.querySelector('[data-ssq-result-missed]');
      if (missed) {
        missed.textContent = copy.missed;
        missed.hidden = !copy.missed;
      }

      var profileValues = {
        skin_goal: PROFILE_LABELS.skin_goal[this.answers.skin_goal] || '',
        current_cleanser: PROFILE_LABELS.current_cleanser[this.answers.current_cleanser] || '',
        scent: copy.scent
      };
      node.querySelectorAll('[data-ssq-profile]').forEach(function (cell) {
        var value = profileValues[cell.getAttribute('data-ssq-profile')];
        cell.textContent = value;
        cell.parentElement.hidden = !value;
      });

      var list = node.querySelector('[data-ssq-result-list]');
      list.querySelectorAll('[data-ssq-pick]').forEach(function (pick) { pick.hidden = true; });
      result.items.forEach(function (item) {
        var pick = list.querySelector('[data-ssq-pick][data-handle="' + item.handle + '"]');
        if (!pick) return;
        pick.querySelector('[data-ssq-pick-quantity]').textContent = item.quantity + ' ×';
        pick.hidden = false;
        list.appendChild(pick);
      });

      var total = node.querySelector('[data-ssq-result-total]');
      if (total) {
        var showPrice = this.dataset.showPrice === 'true';
        total.hidden = !showPrice;
        if (showPrice) total.textContent = 'Six bars at regular price: ' + this.formatMoney(this.totalCents(result));
      }

      node.querySelectorAll('[data-ssq-proof]').forEach(function (module) {
        module.hidden = module.getAttribute('data-ssq-proof') !== self.answers.switching_concern;
      });
      node.querySelectorAll('[data-ssq-cleanser]').forEach(function (line) {
        line.hidden = line.getAttribute('data-ssq-cleanser') !== self.answers.current_cleanser;
      });

      var link = node.querySelector('[data-ssq-offer-link]');
      link.setAttribute('href', this.engine.buildOfferUrl(
        this.dataset.offerUrl,
        result.slots,
        result.ruleVersion,
        window.location.origin
      ));

      this.goTo('results', { push: push });
      this.announce(copy.title + '. ' + result.items.map(function (item) {
        return item.quantity + ' ' + item.title;
      }).join(', ') + '.');
      this.track('quiz_result_view', {
        ruleVersion: result.ruleVersion,
        schemaVersion: this.engine.SCHEMA_VERSION,
        signature: this.engine.signature(result.slots)
      });
    }

    renderUnfulfillable(result, push) {
      var node = this.stepNode('unfulfillable');
      var text = node.querySelector('[data-ssq-unfulfillable-text]');
      text.textContent = 'We can’t fill six bars from the scents you picked today. Pick a few more scents and we’ll build your routine.';
      this.snapshot = null;
      this.goTo('unfulfillable', { push: push });
      this.track('quiz_result_unavailable', { reason: result.reason });
    }

    totalCents(result) {
      var self = this;
      return result.items.reduce(function (sum, item) {
        var product = self.config.catalog.filter(function (entry) { return entry.handle === item.handle; })[0];
        return sum + (product ? product.priceCents * item.quantity : 0);
      }, 0);
    }

    formatMoney(cents) {
      var currency = this.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency }).format(cents / 100);
      } catch (error) {
        console.warn('soft-skin-quiz: currency formatting fell back to a plain number', error);
        return (cents / 100).toFixed(2) + ' ' + currency;
      }
    }

    announce(message) {
      if (this.live) this.live.textContent = message;
    }

    /*
     * Funnel events carry step IDs, versions, and product signatures only.
     * Answer values are never included.
     */
    track(name, detail) {
      var payload = Object.assign({ funnelVersion: this.config.ruleVersion }, detail || {});
      this.dispatchEvent(new CustomEvent('soft-skin-quiz:event', { bubbles: true, detail: { name: name, payload: payload } }));
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('soft_skin_' + name, payload);
        }
      } catch (error) {
        console.warn('soft-skin-quiz: analytics event "' + name + '" was not published', error);
      }
    }
  }

  customElements.define('soft-skin-quiz', SoftSkinQuiz);
})();
